import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import {
  buildPublishBody,
  createPendingIdentity,
  discardPendingIdentity,
  generateRecoveryKey,
  holdSessionKeys,
  publishIdentity,
  publishLanded,
  wrapForRecoveryKey,
} from '../../../services/chat/chatKeys';
import type { PendingIdentity } from '../../../services/chat/chatKeys';
import {
  PASSKEY_UNSUPPORTED_MESSAGE,
  PasskeyError,
  createIdentityPasskey,
  defaultPasskeyName,
} from '../../../services/chat/chatPasskeys';
import type { CreatedPasskey } from '../../../services/chat/chatPasskeys';
import type { UserKeys } from '../../../services/chat/chatService';
import { trustBrowser } from '../../../services/chat/chatTrustedBrowser';
import type { WrappedPrivateKey } from '../../../services/chat/chatCrypto';
import { FreshSignIn } from './FreshSignIn';
import { RecoveryKeyStep } from './RecoveryKeyStep';
import { CHALLENGE_EXPIRED_MESSAGE } from './KeySetupDialog';

export const SIGN_IN_EXPIRED_MESSAGE = 'Your sign-in expired. Sign in again to finish replacing your key.';

type ResetStep = 'preparing' | 'recovery' | 'sign-in' | 'passkey' | 'uploading' | 'unsupported' | 'failed';

export interface IdentityResetDialogProps {
  open: boolean;
  uid: string;
  email: string;
  onClose: () => void;
  /** The replacement identity is published, held in memory, and this browser trusted. */
  onComplete: (keys: UserKeys) => void;
}

/**
 * "Replace my chat encryption key" (unlock gate only). Order per the spec's
 * Recovery section: keypair → prfSalt → recovery key shown and downloaded →
 * full re-entry → the full login again → register/options (identity_reset) →
 * create() (+ PRF fallback) → wrap → POST /keys. The passkey is created only
 * after the sign-in, because an authenticator replaces the existing credential
 * at creation time. A 403 keeps every generated artefact and re-runs the
 * sign-in; a `challenge_expired` asks for one more passkey touch.
 */
export function IdentityResetDialog({ open, uid, email, onClose, onComplete }: IdentityResetDialogProps) {
  const [step, setStep] = useState<ResetStep>('preparing');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [recoveryKey, setRecoveryKey] = useState<string | null>(null);
  const identityRef = useRef<PendingIdentity | null>(null);
  const wrappedRef = useRef<WrappedPrivateKey | null>(null);
  const passkeyRef = useRef<CreatedPasskey | null>(null);
  // After a 403 the sign-in step re-runs and the same body is re-uploaded without a new passkey touch.
  const resumeUploadRef = useRef(false);

  const discard = () => {
    discardPendingIdentity(identityRef.current);
    identityRef.current = null;
    wrappedRef.current = null;
    passkeyRef.current = null;
    resumeUploadRef.current = false;
    setRecoveryKey(null);
  };

  useEffect(() => {
    if (!open) {
      discard();
      return undefined;
    }
    let cancelled = false;
    setStep('preparing');
    setError(null);
    setNotice(null);
    setName(defaultPasskeyName());
    (async () => {
      try {
        const identity = await createPendingIdentity();
        if (cancelled) {
          discardPendingIdentity(identity);
          return;
        }
        identityRef.current = identity;
        setRecoveryKey(generateRecoveryKey());
        setStep('recovery');
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Could not generate a new encryption key.');
          setStep('failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const finish = async (keys: UserKeys) => {
    const identity = identityRef.current;
    if (!identity) return;
    holdSessionKeys(keys.publicKey, identity.privateKey, 'setup');
    await trustBrowser(uid, keys.publicKey, identity.privateKey, keys.trustEpoch);
    identityRef.current = null;
    onComplete(keys);
  };

  const upload = async () => {
    const identity = identityRef.current;
    const wrapped = wrappedRef.current;
    const passkey = passkeyRef.current;
    if (!identity || !wrapped || !passkey) return;
    setStep('uploading');
    setError(null);
    try {
      const keys = await publishIdentity(
        buildPublishBody(identity, wrapped, { name: name.trim() || defaultPasskeyName(), ...passkey }),
      );
      await finish(keys);
    } catch (err: any) {
      if (err?.status === 403 && err?.code === 'reauth_required') {
        resumeUploadRef.current = true;
        setNotice(SIGN_IN_EXPIRED_MESSAGE);
        setStep('sign-in');
        return;
      }
      if (err?.code === 'challenge_expired') {
        passkeyRef.current = null;
        setNotice(CHALLENGE_EXPIRED_MESSAGE);
        setStep('passkey');
        return;
      }
      setError(err?.message || 'Could not publish your new encryption key. Please try again.');
      setStep('failed');
    }
  };

  const handleRecoveryConfirmed = async () => {
    const identity = identityRef.current;
    if (!identity || !recoveryKey) return;
    setBusy(true);
    try {
      wrappedRef.current = await wrapForRecoveryKey(identity.privateKey, recoveryKey);
      setStep('sign-in');
    } finally {
      setBusy(false);
    }
  };

  const handleSignedIn = async () => {
    setNotice(null);
    if (resumeUploadRef.current && passkeyRef.current) {
      resumeUploadRef.current = false;
      await upload();
      return;
    }
    setStep('passkey');
  };

  const handleCreatePasskey = async () => {
    const identity = identityRef.current;
    if (!identity || busy) return;
    setBusy(true);
    setError(null);
    try {
      passkeyRef.current = await createIdentityPasskey({
        prfSalt: identity.prfSalt,
        purpose: 'identity_reset',
        privateKey: identity.privateKey,
      });
      setNotice(null);
      await upload();
    } catch (err: any) {
      if (err instanceof PasskeyError && err.reason === 'prf-unavailable') setStep('unsupported');
      else setError(err?.message || 'Could not create a passkey. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleRetry = async () => {
    const identity = identityRef.current;
    if (!identity) return;
    setBusy(true);
    try {
      const landed = await publishLanded(identity.publicKey).catch(() => null);
      if (landed) {
        await finish(landed);
        return;
      }
      await upload();
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy || step === 'uploading') return;
    discard();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth className="ph-no-capture" PaperProps={{ className: 'ph-no-capture' }} aria-labelledby="identity-reset-title">
      <DialogTitle id="identity-reset-title" sx={{ fontWeight: 600 }}>
        Replace my chat encryption key
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {step === 'preparing' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} aria-label="Generating a new encryption key" />
          </Box>
        )}

        {step === 'recovery' && recoveryKey && (
          <>
            <Alert severity="warning">
              Replacing your key invalidates your passkeys and your old recovery file. Each
              conversation becomes readable again only after a colleague confirms your new key.
            </Alert>
            <RecoveryKeyStep recoveryKey={recoveryKey} email={email} onConfirmed={handleRecoveryConfirmed} disabled={busy} />
          </>
        )}

        {step === 'sign-in' && (
          <>
            {notice && <Alert severity="warning">{notice}</Alert>}
            <FreshSignIn
              uid={uid}
              email={email}
              intro="Sign in again to confirm it is you replacing this account's chat key."
              onDone={handleSignedIn}
              onCancel={handleClose}
              onAbort={discard}
            />
          </>
        )}

        {step === 'passkey' && (
          <>
            <DialogContentText sx={{ fontSize: '0.875rem' }}>
              Create the passkey that will unlock your new key on this and your other devices.
            </DialogContentText>
            {notice && <Alert severity="info">{notice}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Passkey name"
              value={name}
              onChange={(event) => setName(event.target.value.slice(0, 64))}
              size="small"
              fullWidth
              disabled={busy}
              inputProps={{ maxLength: 64 }}
            />
          </>
        )}

        {step === 'uploading' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }} role="status">
            <CircularProgress size={20} />
            <DialogContentText sx={{ fontSize: '0.875rem' }}>Publishing your new encryption key…</DialogContentText>
          </Box>
        )}

        {step === 'unsupported' && <Alert severity="error">{PASSKEY_UNSUPPORTED_MESSAGE}</Alert>}
        {step === 'failed' && <Alert severity="error">{error || 'Replacing the key failed.'}</Alert>}
      </DialogContent>
      {(step === 'passkey' || step === 'failed' || step === 'unsupported' || step === 'recovery') && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={busy} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          {step === 'passkey' && (
            <Button variant="contained" onClick={handleCreatePasskey} disabled={busy} sx={{ minWidth: 150 }}>
              {busy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create passkey'}
            </Button>
          )}
          {step === 'failed' && (
            <Button variant="contained" onClick={handleRetry} disabled={busy || !wrappedRef.current || !passkeyRef.current}>
              Retry
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
